import {
  FormControl,
  FormLabel,
  MenuItem,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
} from '@material-ui/core';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@material-ui/icons';
import cx from 'classnames';
import { FC, KeyboardEvent, ReactNode, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';

import { t } from '@core/i18n';
import { Ellipsis } from '@shared/components/ellipsis';
import { SearchField } from '@shared/components/search-field';
import { isKeyboardEvent } from '@shared/components/select-new/Select';
import { ButtonSize, NewButtonColour, SelectColour } from '@shared/types/common/button';

import { styles } from './GroupSelect.styles';
import { Option, Value } from '../select/Select.types';

// **************************************************
// * !! DO NOT USE COMPONENT DIRECTLY            !! *
// * !! USE SingleGroupSelect / MultiGroupSelect !! *
// **************************************************

export const filterGroupedOptionsBySearch = (groups: GroupedOption[], search: string) => {
  return groups
    .map(({ options, ...otherProps }) => {
      return {
        ...otherProps,
        options: options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase())),
      };
    })
    .filter(({ options }) => options.length);
};

// **************************************************
// * !! DO NOT USE COMPONENT DIRECTLY            !! *
// * !! USE SingleGroupSelect / MultiGroupSelect !! *
// **************************************************

export interface GroupedOption<T = Id> {
  label: string;
  options: Option<T>[];
}

export type SingleGroupSelectOnChange = (value: Id, option?: Option) => void;

export type MultiGroupSelectOnChange = (value: Id[], option?: Option) => void;

export type GroupSelectOnChange = (value: Id | Id[], option?: Option) => void;

// **************************************************
// * !! DO NOT USE COMPONENT DIRECTLY            !! *
// * !! USE SingleGroupSelect / MultiGroupSelect !! *
// **************************************************

export interface GroupSelectProps
  extends Pick<MuiSelectProps, 'autoFocus' | 'multiple' | 'required' | 'onClose' | 'onOpen' | 'renderValue'>,
    WithStyles<typeof styles> {
  action?: ReactNode;
  active?: boolean;
  colour?: SelectColour;
  // Opens the menu as the select mounts
  defaultOpen?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorText?: string;
  fullWidth?: boolean;
  groupOptions: GroupedOption[];
  hovered?: boolean;
  icon?: ReactNode;
  label?: ReactNode | string;
  placeholder?: string;
  size?: ButtonSize;
  value: Option['id'] | Option['id'][];
  onChange: GroupSelectOnChange;
}

// **************************************************
// * !! DO NOT USE COMPONENT DIRECTLY            !! *
// * !! USE SingleGroupSelect / MultiGroupSelect !! *
// **************************************************

const GroupSelectComponent: FC<GroupSelectProps> = ({
  action,
  active = false,
  autoFocus,
  classes,
  colour = NewButtonColour.Outline,
  defaultOpen = false,
  disabled = false,
  error = false,
  errorText,
  fullWidth = false,
  groupOptions,
  hovered = false,
  icon,
  label,
  multiple = true,
  placeholder,
  required,
  size = ButtonSize.small,
  value,
  onChange,
  onClose,
  onOpen,
  renderValue,
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(defaultOpen);

  const handleClose = (e?: React.ChangeEvent<any>) => {
    if (onClose && e) {
      onClose(e);
    }

    setOpen(false);

    // A menu closed from the keyboard leaves focus where the keyboard put it, so a further Escape reaches what the
    // select sits in
    if (e && isKeyboardEvent(e)) {
      return;
    }

    setTimeout(() => {
      // @ts-ignore
      document?.activeElement?.blur();
    }, 0);
  };

  const handleClick = (option: Option, e: React.MouseEvent<HTMLLIElement>) => {
    if (multiple) {
      if (valuesSet.has(option.id)) {
        onChange(
          (value as Option['id'][]).filter((el) => el !== option.id),
          option
        );
      } else {
        onChange([...(value as Option['id'][]), option.id], option);
      }
    } else {
      handleClose(e);
      onChange(option.id, option);
      return;
    }
  };

  const options = useMemo(() => {
    const initial: Option[] = [];

    return groupOptions.reduce((acc, el) => [...acc, ...el.options], initial);
  }, [groupOptions]);

  const valuesSet = useMemo(() => {
    if (Array.isArray(value)) {
      return new Set(value);
    }

    return new Set([value]);
  }, [value]);

  const placeholderVisible = useMemo(() => {
    if (multiple) {
      return !value || (Array.isArray(value) && value.length === 0);
    } else {
      return !value;
    }
  }, [value]);

  const renderSelectorValue = (data: Value | Value[]) => {
    if (renderValue && data) {
      return renderValue(data as Value);
    }

    let valueContent = <></>;
    if (placeholderVisible) {
      // Show placeholder
      valueContent = (
        <Ellipsis withTooltip={false} maxWidth="100%" text={placeholder} classes={{ root: classes.valuePlaceholder }} />
      );
    } else {
      const getOption = (id: Value | Value[]) => {
        return options.find((option) => option.id == id) as Option;
      };

      // Render option label
      let labelContent = '';
      if (multiple && Array.isArray(data)) {
        // Multi select
        labelContent = data.map((id) => getOption(id)?.label).join(', ');
      } else if (!multiple && typeof data !== 'undefined') {
        // Single select
        labelContent = getOption(data as Value)?.label || '';
      }

      valueContent = <span className={classes.valueText} dangerouslySetInnerHTML={{ __html: labelContent }} />;
    }

    return (
      <div
        className={cx(classes.value, classes[size], {
          [classes.valueDisabled]: disabled,
          [classes.valueMultiple]: multiple,
        })}
      >
        {!!icon && <span className={classes.valueIcon}>{icon}</span>}
        {valueContent}
      </div>
    );
  };

  // The options sit in groups below the search, out of the menu list's own reach, so the arrows move between them here
  const handleMenuKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const list = event.currentTarget.closest('[role="listbox"]');

    if (!list) return;

    const searchInput = list.querySelector<HTMLInputElement>('input');
    const inSearch = !event.currentTarget.matches('[data-group-select-option]');

    // The search filters the options after a pause, so an arrow pressed straight after typing filters them first
    if (inSearch && event.key === 'ArrowDown' && searchInput && searchInput.value !== search) {
      flushSync(() => setSearch(searchInput.value));
    }

    const optionElements = Array.from(list.querySelectorAll<HTMLElement>('[data-group-select-option]'));
    const index = optionElements.indexOf(event.currentTarget);
    let target: HTMLElement | null | undefined;

    if (event.key === 'ArrowDown') {
      target = optionElements[index + 1];
    } else if (event.key === 'ArrowUp') {
      target = inSearch ? undefined : optionElements[index - 1] ?? searchInput;
    } else if (!inSearch && event.key === 'Home') {
      target = optionElements[0];
    } else if (!inSearch && event.key === 'End') {
      target = optionElements[optionElements.length - 1];
    } else if (!inSearch && event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey) {
      // Typing from an option carries on into the search
      searchInput?.focus();
      event.stopPropagation();
      return;
    } else {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    target?.focus();
  };

  const renderOption = (option: Option) => {
    return (
      <MenuItem
        key={option.id}
        data-group-select-option
        onClick={(e) => handleClick(option, e)}
        onKeyDown={handleMenuKeyDown}
        selected={valuesSet.has(option.id)}
        classes={{
          root: classes.menuItem,
          selected: classes.menuItemActive,
        }}
      >
        {option.label}
      </MenuItem>
    );
  };

  const hasError = useMemo(() => error && !!errorText, [error, errorText]);

  return (
    <FormControl
      size="small"
      variant="outlined"
      classes={{
        root: cx(classes.root, classes[colour], classes[size], {
          [classes.selectActive]: active,
          [classes.selectHovered]: hovered,
          [classes.selectDisabled]: disabled,
          [classes.rootFullWidth]: fullWidth,
        }),
      }}
    >
      {label && (
        <FormLabel
          classes={{ root: classes.labelRoot, error: classes.labelError }}
          component="legend"
          error={error}
          required={required}
        >
          {label}
          {action}
        </FormLabel>
      )}
      <MuiSelect
        autoFocus={autoFocus}
        classes={{ root: cx(classes.select, classes[size]) }}
        defaultValue={placeholder}
        displayEmpty
        disabled={disabled}
        error={error}
        IconComponent={KeyboardArrowDownIcon}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          classes: { paper: classes.menu },
          getContentAnchorEl: null,
        }}
        multiple={true}
        open={open}
        value={multiple && Array.isArray(value) ? value : [value]}
        onClick={() => {
          if (!disabled && !open) {
            setOpen(true);
          }
        }}
        onClose={handleClose}
        onOpen={(event) => {
          // A click opens the menu above; a key that opens a select, such as ArrowDown, arrives here only
          if (event.type === 'keydown' && !disabled) {
            setOpen(true);
          }

          onOpen?.(event);
        }}
        renderValue={(data) => renderSelectorValue(data as Value | Value[])}
      >
        <SearchField
          fullWidth
          placeholder={t('xtextx_ellipsis', { text: t('search') })}
          inputProps={{ onKeyDown: handleMenuKeyDown }}
          search={search}
          onChange={setSearch}
          classes={{ root: classes.search, inputClass: classes.searchInput }}
        />
        {filterGroupedOptionsBySearch(groupOptions, search).map(({ label, options }, idx) => {
          return (
            <div className={classes.group} key={label || idx}>
              <div className={classes.groupItem}>
                {label && <span className={classes.groupLabel}>{label}</span>}
                {options.map((option) => renderOption(option))}
              </div>
            </div>
          );
        })}
      </MuiSelect>
      {hasError && <Ellipsis classes={{ root: classes.errorText }} text={errorText} />}
    </FormControl>
  );
};

// **************************************************
// * !! DO NOT USE COMPONENT DIRECTLY            !! *
// * !! USE SingleGroupSelect / MultiGroupSelect !! *
// **************************************************

export const GroupSelect = withStyles(styles)(GroupSelectComponent);
